import {decode} from '@mapbox/polyline';
import i18n from 'i18next';
import apiClient from '../../services/api-client';
import {GOOGLE_MAPS_API_KEY} from '../../config/apis';

// The backend's distance/duration strings are in miles, so format the raw
// numbers here (NL market: km, minutes).
export const formatDistance = meters =>
  meters >= 1000
    ? `${(meters / 1000).toFixed(1)} km`
    : `${Math.round(meters)} m`;

export const formatDuration = seconds => {
  const mins = Math.max(1, Math.round(seconds / 60));
  if (mins < 60) {
    return `${mins} min`;
  }
  const hour = i18n.language === 'dt' ? 'u' : 'h';
  const rest = mins % 60;
  return rest
    ? `${Math.floor(mins / 60)} ${hour} ${rest} min`
    : `${Math.floor(mins / 60)} ${hour}`;
};

// Driving routes between two points from POST maps/routes: Google's
// recommended route first, then up to two alternatives.
// Resolves to [] when no route exists; throws on network/API errors.
export const fetchRouteOptions = async (start, end) => {
  let res;
  try {
    res = await apiClient.post('maps/routes', {
      sourceLat: start.latitude,
      sourceLng: start.longitude,
      destLat: end.latitude,
      destLng: end.longitude,
    });
  } catch (err) {
    // 400 "Could not find routes": there is no driving route.
    if (err.response?.status === 400) {
      return [];
    }
    throw err;
  }

  const list = (res.data?.data ?? res.data?.Data ?? []).filter(
    r => typeof r?.polyline === 'string' && r.polyline,
  );
  // Older backend builds answer with an empty polyline; ask Google directly
  // then, so the map still has real routes to draw and send back.
  if (list.length === 0) {
    return fetchGoogleRouteOptions(start, end);
  }
  return list.map((r, index) => ({
      id: `route-${index}`,
      summary: r.summary,
      labels: r.routeLabels || [],
      durationSec: r.durationSeconds,
      durationText: formatDuration(r.durationSeconds),
      distanceMeters: r.distanceMeters,
      distanceText: formatDistance(r.distanceMeters),
      // The backend doesn't say how much traffic adds.
      hasDelay: false,
      // Sent back unchanged to maps/gas-stations for this route's pumps.
      polyline: r.polyline,
      coordinates: decode(r.polyline).map(([lat, lng]) => ({
        latitude: lat,
        longitude: lng,
      })),
    }));
};

// Same result shape, straight from Google Directions (alternatives, live
// traffic). Fallback for backends without maps/routes polylines.
const fetchGoogleRouteOptions = async (start, end) => {
  const params = [
    `origin=${start.latitude},${start.longitude}`,
    `destination=${end.latitude},${end.longitude}`,
    'mode=driving',
    'alternatives=true',
    'departure_time=now',
    `language=${i18n.language === 'dt' ? 'nl' : 'en'}`,
    `key=${GOOGLE_MAPS_API_KEY}`,
  ].join('&');
  const resp = await fetch(
    `https://maps.googleapis.com/maps/api/directions/json?${params}`,
  );
  const json = await resp.json();
  if (json.status === 'ZERO_RESULTS') {
    return [];
  }
  if (json.status !== 'OK') {
    throw new Error(json.error_message || json.status);
  }

  return json.routes.map((r, index) => {
    const leg = r.legs[0];
    const duration = leg.duration_in_traffic || leg.duration;
    return {
      id: `route-${index}`,
      summary: r.summary,
      labels: [],
      durationSec: duration.value,
      durationText: formatDuration(duration.value),
      distanceMeters: leg.distance.value,
      distanceText: formatDistance(leg.distance.value),
      // Traffic adds more than 10% to the typical time.
      hasDelay:
        !!leg.duration_in_traffic &&
        leg.duration_in_traffic.value > leg.duration.value * 1.1,
      polyline: r.overview_polyline.points,
      coordinates: decode(r.overview_polyline.points).map(([lat, lng]) => ({
        latitude: lat,
        longitude: lng,
      })),
    };
  });
};

const sqDist = (a, b) => {
  const dLat = a.latitude - b.latitude;
  const dLng = a.longitude - b.longitude;
  return dLat * dLat + dLng * dLng;
};

// Where to pin each route's "29 min" label: the point of that route lying
// farthest from every other route, so labels sit on the part of the line
// that is visibly its own. Only the middle 60% is considered so labels
// don't cover the start/end pins.
export const getLabelPoints = routes =>
  routes.map((route, i) => {
    const pts = route.coordinates;
    if (pts.length === 0) {
      return null;
    }
    const others = routes
      .filter((_, j) => j !== i)
      .map(r => r.coordinates.filter((_, k) => k % 3 === 0));
    if (others.length === 0) {
      return pts[Math.floor(pts.length / 2)];
    }
    let best = pts[Math.floor(pts.length / 2)];
    let bestScore = -1;
    const from = Math.floor(pts.length * 0.2);
    const to = Math.ceil(pts.length * 0.8);
    for (let k = from; k < to; k += 2) {
      let nearest = Infinity;
      for (const other of others) {
        for (const q of other) {
          const d = sqDist(pts[k], q);
          if (d < nearest) {
            nearest = d;
          }
        }
      }
      if (nearest > bestScore) {
        bestScore = nearest;
        best = pts[k];
      }
    }
    return best;
  });
